import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import theme from './theme';

export default function EfficacyAuditDownload({ reports, monthLabel }) {
  const [open, setOpen] = useState(false);
  const withLinks = (Array.isArray(reports) ? reports : []).filter((r) => String(r.fileLink || '').trim());

  const disabled = withLinks.length === 0;

  return (
    <>
      <Box
        sx={{
          p: 2.5,
          borderRadius: `${theme.radius.lg}px`,
          border: `1px dashed ${theme.colors.borderDefault}`,
          bgcolor: theme.colors.bgSecondary,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary, mb: 0.5 }}>
          Audit-ready lab reports
        </Typography>
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mb: 2, lineHeight: 1.5 }}>
          {disabled
            ? 'No linked PDFs for this viewing month.'
            : `${withLinks.length} report${withLinks.length === 1 ? '' : 's'} available for ${monthLabel || 'this period'}.`}
        </Typography>
        <Button
          variant="outlined"
          fullWidth
          disabled={disabled}
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: theme.colors.borderDefault,
            color: theme.colors.textPrimary,
            maxWidth: 480,
            mx: 'auto',
            display: 'flex',
          }}
        >
          Download lab-report bundle for this period
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: `${theme.radius.lg}px` } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Lab reports — {monthLabel || 'period'}</Typography>
          <IconButton aria-label="Close" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
            Open each report to save or print. A single ZIP bundle is not available yet.
          </Typography>
          <List dense disablePadding>
            {withLinks.map((r) => {
              const label = r.fileName || r.date || r.dateISO || 'Lab report';
              const href = String(r.fileLink).trim();
              return (
                <ListItem
                  key={r.swabId || href}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderRadius: `${theme.radius.md}px`,
                    mb: 0.5,
                    border: `1px solid ${theme.colors.borderDefault}`,
                    '&:hover': { bgcolor: theme.colors.bgSecondary },
                  }}
                >
                  <ListItemText
                    primary={label}
                    secondary={r.date || r.dateISO || undefined}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                  <DownloadOutlinedIcon sx={{ color: theme.colors.brandPrimary, fontSize: 20 }} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
